import { Box, ButtonGroup, Paper, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { getTabStyle } from '@verantos-dev/core-annotation-tool';
import { Button } from '@verantos-dev/shared-components';

import { BatchProgressTable } from './BatchProgressTable';
import { useBatchProgress } from './useBatchProgress';
import { BATCH_PROGRESS_TAB_OPTIONS } from '../../constants';
import { BatchwiseAnnotationProgress } from '../../types/responseTypes';

interface BatchProgressProps {
  annotationProgressBatchWiseData: BatchwiseAnnotationProgress[];
}
export const BatchProgress = ({ annotationProgressBatchWiseData }: BatchProgressProps) => {
  const {
    chartData,
    chartSeries,
    selectedBatchwiseProgressTab,
    handleTabChange,
    isBatchwiseAnnotationProgressDateRangeLoading,
    minDate,
    maxDate,
    startDate,
    endDate,
    handleChangeStartDate,
    handleChangeEndDate,
    shouldDisableStartDate,
  } = useBatchProgress({
    annotationProgressBatchWiseData,
  });
  const theme = useTheme();
  const { formatMessage } = useIntl();
  return (
    <Paper
      sx={{
        p: theme.spacing(6),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
      }}
    >
      <Typography variant="h6">
        {formatMessage({ id: 'metrics.dashboard.batchProgress.title' })}
      </Typography>
      <Box sx={{ display: 'flex', gap: theme.spacing(6) }}>
        <DatePicker
          label={formatMessage({ id: 'metrics.dashboard.batchProgress.datepicker.label.start' })}
          value={startDate}
          onChange={(newValue) => handleChangeStartDate(newValue)}
          minDate={dayjs(minDate)}
          maxDate={endDate ?? undefined}
          disabled={isBatchwiseAnnotationProgressDateRangeLoading}
          shouldDisableDate={shouldDisableStartDate}
          slotProps={{
            textField: {
              sx: {
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              },
            },
          }}
        />
        <DatePicker
          label={formatMessage({ id: 'metrics.dashboard.batchProgress.datepicker.label.end' })}
          value={endDate}
          onChange={(newValue) => handleChangeEndDate(newValue)}
          minDate={startDate ?? undefined}
          maxDate={dayjs(maxDate)}
          disabled={isBatchwiseAnnotationProgressDateRangeLoading}
          slotProps={{
            textField: {
              sx: {
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              },
            },
          }}
        />
      </Box>
      <Box>
        <ButtonGroup
          color="inherit"
          orientation="horizontal"
          variant="contained"
          sx={{ borderRadius: 0 }}
        >
          {BATCH_PROGRESS_TAB_OPTIONS.map((option: string) => (
            <Button
              key={option}
              id={option}
              displayLabel={formatMessage({
                id: `metrics.dashboard.batchProgress.tab.${option}`,
              })}
              sx={getTabStyle(option, selectedBatchwiseProgressTab, theme)}
              onClickHandler={() => handleTabChange(option)}
            />
          ))}
        </ButtonGroup>
      </Box>
      {selectedBatchwiseProgressTab == BATCH_PROGRESS_TAB_OPTIONS[0] ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: chartData.length * 100 }}>
            <BarChart
              dataset={chartData as any}
              xAxis={[
                {
                  scaleType: 'band',
                  dataKey: 'batch',
                  label: formatMessage({
                    id: 'metrics.dashboard.batchProgress.xAxis.label',
                  }),
                },
              ]}
              yAxis={[
                {
                  label: formatMessage({
                    id: 'metrics.dashboard.batchProgress.yAxis.label',
                  }),
                },
              ]}
              series={chartSeries}
              height={300}
            />
          </Box>
        </Box>
      ) : (
        <Box>
          <BatchProgressTable />
        </Box>
      )}
    </Paper>
  );
};
